import Chart, { ScatterDataPoint, ChartData, ChartConfiguration, ChartEvent } from 'chart.js/auto'
import zoomPlugin from 'chartjs-plugin-zoom'
import { merge } from 'lodash'
import moment from 'moment'
import IWSChart from '@/lib/graph/IWSChart/IWSChart'
import { getAxes } from '@/lib/graph/IWSChart/IWSAxes'
import { useInitDataStore } from '@/stores/initDataStore'
import { HistoryDataService } from '@/lib/graph/historyDataService'
import { XRange } from '@/lib/graph/types/initResult'

Chart.register(zoomPlugin)

const DEFAULT_OPTIONS = {
  type: 'line',
  plugins: [
    {
      id: 'minimap-plugin',
    },
  ],
  options: {
    animation: {
      duration: 1,
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
        intersect: true,
      },
      zoom: {
        pan: {
          enabled: true,
          mode: 'x',
          threshold: 0,
        },
      },
    },
  },
}

export const OVERLAP_DIRECTION = {
  LEFT: -1, // 左に空白あり
  OVERLAP: 0, // 包含している
  RIGHT: 1, // 右側に空白あり
}

export default class IWSMiniChart extends IWSChart {
  service: HistoryDataService

  constructor(
    public canvas: HTMLCanvasElement,
    public selectedFields: string[],
    public reportType: string,
    public onClickCallback: (newCenter: number) => void,
    public onPanCallback: (newCenter: number) => void,
    public onPanCompleteCallback: (newCenter: number) => void,
    public onAfterDrawCallback: () => void
  ) {
    super(canvas)
    this.service = new HistoryDataService()
  }

  render(data: ChartData) {
    const initDataStore = useInitDataStore()
    const initData = initDataStore.initData
    const threshold = initData.definitionInfo.graph.threshold
    const selectedField = this.selectedFields.length == 1 ? this.selectedFields[0] : undefined
    const units = this.selectedFields.map(f => initDataStore.getUnit(f))
    const scales = getAxes(false, this.selectedFields, this.reportType, units, threshold, selectedField)
    const options = merge({}, DEFAULT_OPTIONS, {
      data,
      plugins: [
        {
          id: 'minimap-plugin',
          afterDraw: () => this.onAfterDrawCallback(),
        },
      ],
      options: {
        scales,
        plugins: {
          zoom: {
            pan: {
              onPan: () => this.onPan(),
              onPanComplete: () => this.onPanComplete(),
            },
          },
        },
        onClick: (e: ChartEvent) => this.onClick(e),
      },
    }) as ChartConfiguration<'line'>

    this._render(options)
  }

  onPan() {
    const center = this.getCenter()

    if (center) {
      this.onPanCallback(center)
    }
  }

  onPanComplete() {
    const center = this.getCenter()

    if (center) {
      this.onPanCompleteCallback(center)
    }
  }

  onClick(event: ChartEvent) {
    if (!this.chartjs) {
      return
    }

    const nativeEvent = event.native as MouseEvent
    const x = nativeEvent.clientX
    const element = this.canvas
    const { left: minX, right: maxX } = element.getBoundingClientRect()
    const rate = Math.floor(x - minX) / Math.floor(maxX - minX)
    const { min, max } = this.chartjs.scales['x'].getMinMax(true)
    const clickedTime = Math.floor(min + Math.floor((max - min) * rate))

    this.onClickCallback(clickedTime)

    const { start, end } = this.getRange(clickedTime)
    this.chartjs.zoomScale('x', { min: start, max: end })
    this.chartjs.update()
  }

  getRange(center: number, reportType?: string) {
    const time = center
    let start = moment(time)
    let end = moment(time)
    const type = reportType || this.reportType

    switch (type) {
      case XRange.HOUR:
        start = start.subtract(Math.floor(8 * 3.5), 'hour').startOf('hour')
        end = end.add(Math.floor(8 * 3.5) - 1, 'hour').endOf('hour')
        break
      case XRange.DAY:
        start = start.subtract(12 * 7, 'hour').startOf('hour')
        end = end.add(12 * 7 - 1, 'hour').endOf('hour')
        break
      case XRange.MONTH:
        start = start.subtract(Math.floor(15 * 7), 'day').startOf('day')
        end = end.add(Math.floor(15 * 7 - 1), 'day').endOf('day')
        break
      case XRange.YEAR:
        start = start.subtract(6 * 7, 'month').startOf('month')
        end = end.add(6 * 7 - 1, 'month').endOf('month')
        break
    }
    const startTime = start.toDate().getTime()
    const endTime = end.add(1, 'minute').toDate().getTime()

    return {
      start: startTime,
      end: endTime,
    }
  }

  /*
   * 保持するdatasetsが引数centerより計算される表示領域に包含しているかどうか
   * @return
   *   0(OVERLAP_DIRECTION.OVERLAP): 包含している
   *  -1(OVERLAP_DIRECTION.LEFT):    過去に取得していない空白がある形
   *   1(OVERLAP_DIRECTION.RIGHT):    未来に取得していない空白がある形
   */
  overlapRange(center: number) {
    // 描画されているグラフの両端の計測時間
    const { start, end } = this.getRange(center)
    const now = moment().valueOf()

    // 保持データの両端の時間
    const plotMin = (this.min as ScatterDataPoint)?.x
    const plotMax = (this.max as ScatterDataPoint)?.x

    if (!plotMin || !plotMax) {
      return
    }

    // 現在時刻を超過している場合は計測データが存在しないため包含しているとみなす
    if (now < end) {
      return OVERLAP_DIRECTION.OVERLAP
    }

    if (plotMin > start) {
      return OVERLAP_DIRECTION.LEFT
    }

    if (plotMax < end) {
      return OVERLAP_DIRECTION.RIGHT
    }

    return OVERLAP_DIRECTION.OVERLAP
  }

  getTimeString(center: number, type: 'start' | 'end', _reportType?: string) {
    const reportType = _reportType || this.reportType
    const range = this.getRange(center, reportType)
    const newTime = moment(type === 'start' ? range.start : range.end)

    switch (reportType) {
      case 'day':
      case 'hour':
        return newTime.format('M/D')
      case 'month':
        return newTime.format('YYYY/MM')
      case 'year':
        return newTime.format('YYYY年')
    }

    return moment(newTime).format('M/D')
  }

  // 基準時間centerより前後の枠の検索範囲を取得
  static getMarginRange(center: number, reportType: string) {
    const time = center
    let start = moment(time)
    let end = moment(time)
    const type = reportType

    switch (type) {
      case XRange.HOUR:
        start = start.subtract(72, 'hour').startOf('hour')
        end = end.add(72, 'hour').endOf('hour')
        break
      case XRange.DAY:
        start = start.subtract(3 * 3, 'day').startOf('hour')
        end = end.add(3 * 3, 'day').endOf('hour')
        break
      case XRange.MONTH:
        start = start.subtract(3 * 3, 'month').startOf('day')
        end = end.add(3 * 3, 'month').endOf('day')
        break
      case XRange.YEAR:
        start = start.subtract(5 * 3, 'year').startOf('month')
        end = end.add(5 * 3, 'year').endOf('month')
        break
    }
    const startTime = start.toDate().getTime()
    const endTime = end.toDate().getTime()

    return {
      start: startTime,
      end: endTime,
    }
  }

  // 基準時間timeより以前の枠の検索範囲を取得
  static getMarginUnderRange(time: number, reportType: string) {
    let center = moment()
    const endTime = moment(time)

    switch (reportType) {
      case XRange.HOUR:
        center = endTime.subtract(72, 'hour').startOf('hour')
        break
      case XRange.DAY:
        center = endTime.subtract(3 * 3, 'day').startOf('hour')
        break
      case XRange.MONTH:
        center = endTime.subtract(3 * 3, 'month').startOf('day')
        break
      case XRange.YEAR:
        center = endTime.subtract(5 * 3, 'year').startOf('month')
        break
    }

    return this.getMarginRange(center.valueOf(), reportType)
  }

  // 基準時間timeより以後の枠の検索範囲を取得
  static getMarginOverRange(time: number, reportType: string) {
    let center = moment()
    const endTime = moment(time)

    switch (reportType) {
      case XRange.HOUR:
        center = endTime.add(72, 'hour').startOf('hour')
        break
      case XRange.DAY:
        center = endTime.add(3 * 3, 'day').startOf('hour')
        break
      case XRange.MONTH:
        center = endTime.add(3 * 3, 'month').startOf('day')
        break
      case XRange.YEAR:
        center = endTime.add(5 * 3, 'year').startOf('month')
        break
    }

    return this.getMarginRange(center.valueOf(), reportType)
  }
}
